/**
 * An owner's voice note, recorded in the browser (spec §8 "Voice input"): `MediaRecorder`,
 * `audio/webm`, two minutes at most. Nothing leaves the browser here: the recording is handed to
 * the caller, which asks the owner to confirm the price before it is uploaded. A discarded
 * recording costs nothing.
 *
 * `unsupported` when the browser has no recorder; `denied` when she said no to the microphone.
 * Either way the box she types in still works.
 */
import { onBeforeUnmount, ref, shallowRef } from 'vue';

import { VOICE_MAX_SECONDS } from '../helpers/setup-ai.js';

const hasRecorder = () =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder === 'function' &&
  typeof window.navigator?.mediaDevices?.getUserMedia === 'function';

export const useVoiceNote = ({
  maxSeconds = VOICE_MAX_SECONDS,
  now = () => Date.now(),
} = {}) => {
  /** `idle` | `recording` | `recorded` | `unsupported` | `denied` */
  const state = ref(hasRecorder() ? 'idle' : 'unsupported');
  const seconds = ref(0);
  const blob = shallowRef(null);

  let recorder = null;
  let stream = null;
  let chunks = [];
  let startedAt = 0;
  let ticker = null;

  const release = () => {
    if (ticker !== null) clearInterval(ticker);
    ticker = null;
    stream?.getTracks?.().forEach(track => track.stop());
    stream = null;
  };

  const stop = () => {
    if (recorder !== null && recorder.state === 'recording') recorder.stop();
  };

  const start = async () => {
    if (state.value === 'unsupported' || state.value === 'recording') return;
    blob.value = null;
    seconds.value = 0;
    chunks = [];
    try {
      stream = await window.navigator.mediaDevices.getUserMedia({
        audio: true,
      });
    } catch {
      state.value = 'denied';
      return;
    }
    recorder = new window.MediaRecorder(stream, { mimeType: 'audio/webm' });
    recorder.ondataavailable = event => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      seconds.value = Math.min(maxSeconds, (now() - startedAt) / 1000);
      blob.value = new Blob(chunks, { type: 'audio/webm' });
      state.value = 'recorded';
      release();
    };
    startedAt = now();
    recorder.start();
    state.value = 'recording';
    ticker = setInterval(() => {
      seconds.value = (now() - startedAt) / 1000;
      if (seconds.value >= maxSeconds) stop();
    }, 250);
  };

  const discard = () => {
    stop();
    release();
    blob.value = null;
    seconds.value = 0;
    if (state.value !== 'unsupported') state.value = 'idle';
  };

  onBeforeUnmount(discard);

  return { state, seconds, blob, start, stop, discard };
};

export default useVoiceNote;
